/**
 * Client Selection Page Component
 * Allows admin users to select which client workspace to access
 * 
 * This page is only shown to admin users after login.
 * Regular client users are redirected directly to their dashboard.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, PortalClient } from '../auth';
import './SelectClient.css';
import ConstellationBackground from '../components/ConstellationBackground';

const SelectClient: React.FC = () => {
  const navigate = useNavigate();
  const [clients, setClients] = useState<PortalClient[]>([]);
  const [selectedClientId, setSelectedClientId] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    // Check if user is authenticated and is an admin
    if (!auth.isAuthenticated()) {
      navigate('/login');
      return;
    }

    if (!auth.isAdminPortalUser()) {
      // Not an admin user - redirect to dashboard
      navigate('/dashboard');
      return;
    }

    // Load available clients
    const availableClients = auth.getAvailableClients();
    if (availableClients.length === 0) {
      setError('No clients available. Please contact support.');
      return;
    }

    setClients(availableClients);
  }, [navigate]);

  const handleSelectClient = () => {
    if (!selectedClientId) {
      setError('Please select a client workspace');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Store the selected client
      auth.setSelectedClient(selectedClientId);
      
      // Redirect to dashboard
      navigate('/dashboard');
    } catch (err: any) {
      setError(err.message || 'Failed to select client');
      setLoading(false);
    }
  };

  const user = auth.getUser();

  return (
    <div className="select-client-container">
      <ConstellationBackground />
      <div className="select-client-logo-header">
        <img 
          src="/heat-logo.png" 
          alt="HEAT Logo" 
          className="select-client-logo"
        />
        <p className="select-client-tagline">Strategic Intelligence Platform</p>
      </div>
      
      <div className="select-client-box">
        <div className="select-client-header">
          <h1>Select Client Workspace</h1>
          <p>Welcome, {user?.fullName || user?.firstName || 'Admin'}</p>
          <span className="admin-badge">Admin Access</span>
        </div>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        <div className="client-list">
          {clients.map((client) => (
            <div
              key={client.id}
              className={`client-card ${selectedClientId === client.id ? 'selected' : ''}`}
              onClick={() => setSelectedClientId(client.id)}
            >
              <div className="client-icon">
                {client.name.charAt(0).toUpperCase()}
              </div>
              <div className="client-info">
                <h3>{client.name}</h3>
                <p>Click to select</p>
              </div>
              {selectedClientId === client.id && (
                <div className="check-icon">✓</div>
              )}
            </div>
          ))}
        </div>

        <button
          className="continue-button"
          onClick={handleSelectClient}
          disabled={!selectedClientId || loading}
        >
          {loading ? 'Loading...' : 'Continue to Dashboard'}
        </button>

        <div className="select-client-footer">
          <button 
            className="logout-link"
            onClick={() => {
              auth.logout();
              navigate('/login');
            }}
          >
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
};

export default SelectClient;
